import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Header from './components/Header';
import DateRail from './components/DateRail';
import Scoreboard from './components/Scoreboard';
import UpcomingScoreboard from './components/UpcomingScoreboard';
import MatchCenter from './components/MatchCenter';
import DeveloperTools from './components/DeveloperTools';
import TeamsDirectory from './components/TeamsDirectory';
import { getScoreDetails, getScores, publishMatchSnapshot } from './services/scores';
import { getCurrentRecordSnapshots } from './services/currentRecords';
import {
  mergeMatchCollections,
  mergeMatchSnapshots,
} from './data/matchState';

const LIVE_REFRESH_INTERVAL =
  20_000;

const TODAY_REFRESH_INTERVAL =
  60_000;

const FOREGROUND_REFRESH_GUARD =
  2_500;

function localISODate(
  date = new Date()
) {
  return new Date(
    date.getTime() -
      date.getTimezoneOffset() *
        60_000
  )
    .toISOString()
    .slice(0, 10);
}

function getUrlState() {
  const params =
    new URLSearchParams(
      window.location.search
    );

  const requestedDate =
    params.get('date');

  const requestedMatch =
    params.get('match');

  const requestedTeam =
    String(
      params.get('team') || ''
    )
      .trim()
      .toLowerCase();

  const requestedScoresView =
    String(
      params.get(
        'scoresView'
      ) || ''
    )
      .trim()
      .toLowerCase();

  const hash =
    window.location.hash
      .replace('#', '')
      .trim()
      .toLowerCase();

  const matchId =
    requestedMatch &&
    /^[1-9]\d*$/.test(
      requestedMatch
    )
      ? requestedMatch
      : '';

  return {
    date:
      requestedDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(
        requestedDate
      )
        ? requestedDate
        : localISODate(),

    sport: 'women',

    matchId,

    scoresView:
      !matchId &&
      requestedScoresView ===
        'upcoming'
        ? 'upcoming'
        : 'scores',

    view:
      !matchId &&
      (
        requestedTeam ||
        hash === 'teams'
      )
        ? 'teams'
        : 'scores',
  };
}

function getInitialDate() {
  return getUrlState().date;
}

function getInitialMatchId() {
  return getUrlState().matchId;
}

function getInitialView() {
  return getUrlState().view;
}

function getInitialScoresView() {
  return getUrlState()
    .scoresView;
}

function writeBrowserUrl({
  date,
  sport,
  matchId = '',
  view = 'scores',
  scoresView = 'scores',
  preserveTeam = false,
  mode = 'replace',
}) {
  const url = new URL(
    window.location.href
  );

  url.searchParams.set(
    'date',
    date
  );

  url.searchParams.delete('sport');

  if (view === 'teams') {
    url.searchParams.delete(
      'match'
    );

    if (!preserveTeam) {
      url.searchParams.delete(
        'team'
      );
    }

    url.searchParams.delete(
      'scoresView'
    );

    url.hash = 'teams';
  } else {
    url.searchParams.delete(
      'team'
    );

    if (matchId) {
      url.searchParams.set(
        'match',
        matchId
      );
    } else {
      url.searchParams.delete(
        'match'
      );
    }

    if (
      scoresView ===
      'upcoming'
    ) {
      url.searchParams.set(
        'scoresView',
        'upcoming'
      );
    } else {
      url.searchParams.delete(
        'scoresView'
      );
    }

    url.hash = 'scores';
  }

  const method =
    mode === 'push'
      ? 'pushState'
      : 'replaceState';

  window.history[method](
    {},
    '',
    url.toString()
  );
}

function shouldReduceMotion() {
  if (
    typeof window.matchMedia !==
    'function'
  ) {
    return false;
  }

  return window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
}

function scrollToView({
  view,
  focusSearch = false,
}) {
  window.requestAnimationFrame(
    () => {
      window.requestAnimationFrame(
        () => {
          const searchInput =
            focusSearch
              ? document.getElementById(
                  'scoreboard-search-input'
                )
              : null;

          const section =
            document.getElementById(
              view
            );

          const target =
            searchInput ||
            section;

          target?.scrollIntoView({
            behavior:
              shouldReduceMotion()
                ? 'auto'
                : 'smooth',

            block:
              searchInput
                ? 'center'
                : 'start',
          });

          if (!searchInput) {
            return;
          }

          searchInput.focus({
            preventScroll: true,
          });
        }
      );
    }
  );
}

function getShareableGameId(
  game
) {
  const directId = String(
    game?.gameId ||
      game?.id ||
      ''
  ).trim();

  if (
    /^[1-9]\d*$/.test(
      directId
    )
  ) {
    return directId;
  }

  const urlMatch = String(
    game?.gameUrl ||
      game?.detailsApiUrl ||
      ''
  ).match(
    /\/game\/(\d+)/i
  );

  return urlMatch?.[1] || '';
}

function getGameIdentity(game) {
  const directId =
    getShareableGameId(game);

  if (directId) {
    return `id:${directId}`;
  }

  return [
    'match',
    game?.away?.name || '',
    game?.home?.name || '',
    game?.timeLabel || '',
  ]
    .join(':')
    .toLowerCase();
}

function updateSelectedGame(
  selectedGame,
  games
) {
  if (!selectedGame) {
    return null;
  }

  const selectedIdentity =
    getGameIdentity(
      selectedGame
    );

  const refreshedGame =
    games.find(
      (game) =>
        getGameIdentity(game) ===
        selectedIdentity
    );

  if (!refreshedGame) {
    return selectedGame;
  }

  return mergeMatchSnapshots(
    selectedGame,
    refreshedGame
  );
}

function formatUpdatedTime(
  timestamp
) {
  if (!timestamp) {
    return '';
  }

  return new Date(
    timestamp
  ).toLocaleTimeString(
    undefined,
    {
      hour: 'numeric',
      minute: '2-digit',
    }
  );
}

const FEATURE_CARDS = [
  {
    title: 'Teams',

    description:
      'Browse every Division I team, compare records, view rankings, and save favorites.',

    status: 'Available Now',
  },
  {
    title: 'Rankings',

    description:
      'AVCA rankings, RPI, KPI, and conference standings in one place.',

    status: 'Coming Soon',
  },
  {
    title: 'Match Center',

    description:
      'Set-by-set results, match flow, team statistics, and NCAA data.',

    status: 'Available Now',
  },
];

export default function App() {
  const [
    date,
    setDate,
  ] = useState(
    getInitialDate
  );

  const sport = 'women';

  const [
    requestedMatchId,
    setRequestedMatchId,
  ] = useState(
    getInitialMatchId
  );

  const [
    activeView,
    setActiveView,
  ] = useState(
    getInitialView
  );

  const [
    scoresView,
    setScoresView,
  ] = useState(
    getInitialScoresView
  );

  const [
    selectedGame,
    setSelectedGame,
  ] = useState(null);
  const openMatchIdRef = useRef('');
  const [upcomingRefreshKey, setUpcomingRefreshKey] = useState(0);
  const [upcomingRefreshState, setUpcomingRefreshState] = useState({ refreshing: true, refreshError: '', updatedLabel: '' });
  openMatchIdRef.current = String(selectedGame?.gameId || selectedGame?.id || '');

  const [
    state,
    setState,
  ] = useState({
    loading: true,
    refreshing: false,
    games: [],
    error: '',
    refreshError: '',
    updatedAt: null,
  });

  const activeControllerRef =
    useRef(null);

  const requestIdRef =
    useRef(0);

  const modalHistoryPushedRef =
    useRef(false);

  const lastForegroundRefreshRef =
    useRef(0);

  const scoreContextRef =
    useRef('');

  scoreContextRef.current =
    `${date}|${sport}`;

  useEffect(() => {
    writeBrowserUrl({
      date,
      sport,

      matchId:
        requestedMatchId,

      view:
        activeView,

      scoresView,

      preserveTeam:
        activeView ===
        'teams',

      mode: 'replace',
    });
  }, []);

  useEffect(() => {
    function handlePopState() {
      const next =
        getUrlState();

      modalHistoryPushedRef.current =
        false;

      setDate(next.date);
      setActiveView(
        next.view
      );

      setScoresView(
        next.scoresView
      );

      setRequestedMatchId(
        next.matchId
      );

      setSelectedGame(null);

      if (
        next.view ===
        'teams'
      ) {
        activeControllerRef.current
          ?.abort();
      }
    }

    window.addEventListener(
      'popstate',
      handlePopState
    );

    return () => {
      window.removeEventListener(
        'popstate',
        handlePopState
      );
    };
  }, []);

  useEffect(() => {
    return () => {
      activeControllerRef.current
        ?.abort();
    };
  }, []);

  useEffect(() => {
    document.title =
      activeView === 'teams'
        ? 'Teams | VolleyWire'
        : 'VolleyWire';
  }, [activeView]);

  const formattedDate =
    useMemo(
      () =>
        new Date(
          `${date}T12:00:00`
        ).toLocaleDateString(
          undefined,
          {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          }
        ),
      [date]
    );

  const hasLiveGames =
    useMemo(
      () =>
        state.games.some(
          (game) =>
            game?.status ===
            'live'
        ),
      [state.games]
    );

  const selectedDateIsToday =
    date === localISODate();

  const refreshInterval =
    hasLiveGames
      ? LIVE_REFRESH_INTERVAL
      : TODAY_REFRESH_INTERVAL;

  const loadScores =
    useCallback(
      ({
        background = false,
      } = {}) => {
        activeControllerRef.current
          ?.abort();

        const controller =
          new AbortController();

        activeControllerRef.current =
          controller;

        const requestId =
          requestIdRef.current +
          1;

        requestIdRef.current =
          requestId;

        setState(
          (current) => ({
            ...current,

            loading:
              background
                ? current.loading
                : true,

            refreshing:
              background,

            error:
              background
                ? current.error
                : '',

            refreshError: '',
          })
        );

        getScores({
          date,
          sport,

          signal:
            controller.signal,
        })
          .then(
            ({
              games = [],
              stale = false,
            }) => {
              if (
                requestId !==
                requestIdRef.current
              ) {
                return;
              }

              setState((current) => {
                const acceptedGames = background
                  ? mergeMatchCollections(current.games, games)
                  : games;

                return {
                  loading: false,
                  refreshing: false,
                  games: acceptedGames,
                  error: '',
                  refreshError: stale ? 'Refresh failed. Showing the last saved scoreboard; scores may be stale.' : '',
                  updatedAt: Date.now(),
                };
              });

              setSelectedGame(
                (current) =>
                  updateSelectedGame(
                    current,
                    games
                  )
              );
            }
          )
          .catch((error) => {
            if (
              error.name ===
                'AbortError' ||
              requestId !==
                requestIdRef.current
            ) {
              return;
            }

            const message =
              error instanceof
              Error
                ? error.message
                : 'Unable to load scores.';

            setState(
              (current) => {
                const hasExistingGames =
                  current.games.length >
                  0;

                return {
                  ...current,

                  loading: false,
                  refreshing: false,

                  error:
                    hasExistingGames
                      ? ''
                      : message,

                  refreshError:
                    hasExistingGames
                      ? message
                      : '',
                };
              }
            );
          });
      },
      [
        date,
        sport,
      ]
    );

  useEffect(() => {
    if (
      activeView !==
        'scores' ||
      scoresView !==
        'scores'
    ) {
      activeControllerRef.current
        ?.abort();

      return undefined;
    }

    loadScores({
      background: false,
    });

    return undefined;
  }, [
    activeView,
    loadScores,
    scoresView,
  ]);

  useEffect(() => {
    if (
      activeView !==
        'scores' ||
      !requestedMatchId ||
      state.loading
    ) {
      return;
    }

    const sharedGame =
      state.games.find(
        (game) =>
          getShareableGameId(
            game
          ) ===
          requestedMatchId
      );

    if (sharedGame) {
      setSelectedGame(
        sharedGame
      );
    }
  }, [
    activeView,
    requestedMatchId,
    state.games,
    state.loading,
  ]);

  useEffect(() => {
    if (
      activeView !==
        'scores' ||
      scoresView !==
        'scores'
    ) {
      return undefined;
    }

    function refreshVisibleScoreboard() {
      if (
        document.visibilityState !==
          'visible' ||
        state.loading ||
        state.refreshing
      ) {
        return;
      }

      const now =
        Date.now();

      if (
        now -
          lastForegroundRefreshRef.current <
        FOREGROUND_REFRESH_GUARD
      ) {
        return;
      }

      lastForegroundRefreshRef.current =
        now;

      if (state.refreshing || state.loading) return;
      loadScores({
        background: true,
      });
    }

    let intervalId = null;

    if (selectedDateIsToday) {
      intervalId =
        window.setInterval(
          refreshVisibleScoreboard,
          refreshInterval
        );
    }

    function handleVisibilityChange() {
      if (
        document.visibilityState ===
        'visible'
      ) {
        refreshVisibleScoreboard();
      }
    }

    function handleWindowFocus() {
      refreshVisibleScoreboard();
    }

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    window.addEventListener(
      'focus',
      handleWindowFocus
    );

    return () => {
      if (intervalId) {
        window.clearInterval(
          intervalId
        );
      }

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );

      window.removeEventListener(
        'focus',
        handleWindowFocus
      );
    };
  }, [
    activeView,
    loadScores,
    refreshInterval,
    scoresView,
    selectedDateIsToday,
    state.loading,
    state.refreshing,
  ]);

  const navigateToView =
    useCallback(
      (
        nextView,
        {
          mode = 'push',
          focusSearch = false,
        } = {}
      ) => {
        const view =
          nextView === 'teams'
            ? 'teams'
            : 'scores';

        const historyMode =
          view === activeView
            ? 'replace'
            : mode;

        modalHistoryPushedRef.current =
          false;

        setSelectedGame(null);

        setRequestedMatchId('');

        setActiveView(view);

        if (
          view === 'scores'
        ) {
          setScoresView(
            'scores'
          );
        }

        if (view === 'teams') {
          activeControllerRef.current
            ?.abort();
        }

        writeBrowserUrl({
          date,
          sport,
          view,
          scoresView:
            view === 'scores'
              ? 'scores'
              : scoresView,
          matchId: '',
          preserveTeam: false,
          mode: historyMode,
        });

        scrollToView({
          view,
          focusSearch:
            view === 'scores' &&
            focusSearch,
        });
      },
      [
        activeView,
        date,
        scoresView,
        sport,
      ]
    );

  const changeDate =
    useCallback(
      (nextDate) => {
        modalHistoryPushedRef.current =
          false;

        setDate(nextDate);
        setRequestedMatchId('');
        setSelectedGame(null);
        setActiveView(
          'scores'
        );

        setScoresView(
          'scores'
        );

        writeBrowserUrl({
          date: nextDate,
          sport,
          matchId: '',
          view: 'scores',
          scoresView:
            'scores',
          mode: 'replace',
        });
      },
      [sport]
    );

  const changeScoresView =
    useCallback(
      (nextView) => {
        const normalizedView =
          nextView ===
          'upcoming'
            ? 'upcoming'
            : 'scores';

        if (
          normalizedView ===
          scoresView
        ) {
          return;
        }

        modalHistoryPushedRef.current =
          false;

        setScoresView(
          normalizedView
        );

        setRequestedMatchId('');
        setSelectedGame(null);

        if (
          normalizedView ===
          'upcoming'
        ) {
          activeControllerRef.current
            ?.abort();
        }

        writeBrowserUrl({
          date,
          sport,
          matchId: '',
          view: 'scores',
          scoresView:
            normalizedView,
          mode: 'push',
        });
      },
      [
        date,
        scoresView,
        sport,
      ]
    );

  const refreshScores =
    useCallback(() => {
      if (state.refreshing || state.loading) return;
      loadScores({
        background:
          state.games.length > 0,
      });
    }, [
      loadScores,
      state.games.length,
      state.refreshing,
      state.loading,
    ]);

  const hydrateVisibleScores =
    useCallback(
      async (visibleGames, { signal } = {}) => {
        const context = `${date}|${sport}`;
        const applySnapshots = (snapshots) => {
          if (signal?.aborted || !snapshots.length || scoreContextRef.current !== context) return;
          setState((current) => ({ ...current, games: mergeMatchCollections(current.games, snapshots) }));
          setSelectedGame((current) => updateSelectedGame(current, snapshots));
        };
        // Refresh visible records independently of slower box-score hydration.
        const recordsTask = getCurrentRecordSnapshots(visibleGames, date, signal).then(applySnapshots).catch(() => {});
        const details = await getScoreDetails({
          date,
          sport,
          signal,
          onProgress: applySnapshots,
          games: visibleGames.filter((game) => String(game?.gameId || game?.id || '') !== openMatchIdRef.current),
        });

        if (signal?.aborted || !details.length || scoreContextRef.current !== context) {
          await recordsTask;
          return;
        }

        setState((current) => ({
          ...current,
          games: mergeMatchCollections(current.games, details),
        }));

        setSelectedGame((current) =>
          updateSelectedGame(current, details)
        );
        await recordsTask;
      },
      [date, sport]
    );

  const reconcileMatchCenterSnapshot =
    useCallback((snapshot) => {
      if (!snapshot) return;
      publishMatchSnapshot(snapshot, date);

      setState((current) => ({
        ...current,
        games: mergeMatchCollections(current.games, [snapshot]),
      }));

      setSelectedGame((current) =>
        updateSelectedGame(current, [snapshot])
      );
    }, [date]);

  const openMatch =
    useCallback(
      (game) => {
        const gameDate =
          String(
            game?.scheduleDate ||
              date
          ).trim();

        const matchDate =
          /^\d{4}-\d{2}-\d{2}$/.test(
            gameDate
          )
            ? gameDate
            : date;

        const matchId =
          getShareableGameId(
            game
          );

        setActiveView(
          'scores'
        );

        setSelectedGame(game);

        if (
          matchDate !== date
        ) {
          setDate(
            matchDate
          );
        }

        if (!matchId) {
          setRequestedMatchId(
            ''
          );

          return;
        }

        modalHistoryPushedRef.current =
          true;

        setRequestedMatchId(
          matchId
        );

        writeBrowserUrl({
          date: matchDate,
          sport,
          matchId,
          view: 'scores',
          scoresView,
          mode: 'push',
        });
      },
      [
        date,
        scoresView,
        sport,
      ]
    );

  const closeMatch =
    useCallback(() => {
      setSelectedGame(null);
      setRequestedMatchId('');

      if (
        modalHistoryPushedRef.current
      ) {
        modalHistoryPushedRef.current =
          false;

        window.history.back();
        return;
      }

      writeBrowserUrl({
        date,
        sport,
        matchId: '',
        view: 'scores',
        scoresView,
        mode: 'replace',
      });
    }, [
      date,
      scoresView,
      sport,
    ]);

  const updatedTime =
    formatUpdatedTime(
      state.updatedAt
    );

  const updatedLabel =
    state.loading
      ? 'Loading scores…'
      : updatedTime
        ? `Updated ${updatedTime}`
        : '';

  return (
    <div className="app-frame">
      <Header
        activeView={
          activeView
        }
        onNavigate={
          navigateToView
        }
      />

      <main>
        {activeView ===
        'teams' ? (
          <TeamsDirectory
            onBackToScores={() =>
              navigateToView(
                'scores'
              )
            }
          />
        ) : (
          <>
            <section className="hero">
              <div className="shell hero-inner">
                <div>
                  <p className="eyebrow">
                    The Home of College
                    Volleyball
                  </p>

                  <h1>
                    Every Match.
                    <br />

                    <em>
                      Every Moment.
                    </em>
                  </h1>

                  <p className="hero-copy">
                    Live scores,
                    schedules, rankings,
                    standings, and stats
                    from across NCAA
                    volleyball—built
                    specifically for
                    volleyball fans.
                  </p>

                  {import.meta.env.DEV ? (
                    <DeveloperTools
                      date={date}

                      onChangeDate={
                        changeDate
                      }
                    />
                  ) : null}
                </div>

                <div
                  className="hero-orbit"
                  aria-hidden="true"
                >
                  <span>
                    V
                  </span>
                </div>
              </div>
            </section>

            <section
              className="scoreboard-section"
              id="scores"
            >
              <div className="shell">
                <div className="scoreboard-overview scores-mode-overview">
                  <div className="section-heading scoreboard-section-heading">
                    <div>
                      <p className="eyebrow coral">
                        {scoresView ===
                        'upcoming'
                          ? 'Seven-Day Schedule'
                          : 'Live Scoreboard'}
                      </p>

                      <h2>
                        {scoresView ===
                        'upcoming'
                          ? 'Upcoming Week'
                          : formattedDate}
                      </h2>
                    </div>
                  </div>

                  <div className="scoreboard-view-actions">
                    <div
                      className="scores-view-toggle scores-view-toggle-desktop"
                      role="group"
                      aria-label="Scoreboard range"
                    >
                      <button
                        type="button"
                        className={
                          scoresView ===
                          'scores'
                            ? 'active'
                            : ''
                        }
                        aria-pressed={
                          scoresView ===
                          'scores'
                        }
                        aria-label="Daily scores for selected date"
                        onClick={() =>
                          changeScoresView(
                            'scores'
                          )
                        }
                      >
                        Day
                      </button>

                      <button
                        type="button"
                        className={
                          scoresView ===
                          'upcoming'
                            ? 'active'
                            : ''
                        }
                        aria-pressed={
                          scoresView ===
                          'upcoming'
                        }
                        aria-label="Schedule for next seven days"
                        onClick={() =>
                          changeScoresView(
                            'upcoming'
                          )
                        }
                      >
                        7-Day Schedule
                      </button>
                    </div>

                  </div>

                  <DateRail
                    selectedDate={date}
                    view={scoresView}
                    onChangeView={changeScoresView}

                    onChange={
                      changeDate
                    }

                    onRefresh={scoresView === 'upcoming' ? () => setUpcomingRefreshKey(value => value + 1) : refreshScores}
                    updatedLabel={
                      scoresView === 'upcoming' ? upcomingRefreshState.updatedLabel : updatedLabel
                    }

                    refreshing={
                      scoresView === 'upcoming' ? upcomingRefreshState.refreshing : state.refreshing
                    }

                    refreshError={
                      scoresView === 'upcoming' ? upcomingRefreshState.refreshError : state.refreshError
                    }
                  />
                </div>

                {scoresView ===
                'upcoming' ? (
                  <UpcomingScoreboard
                    refreshKey={upcomingRefreshKey}
                    onRefreshState={setUpcomingRefreshState}
                    sport={sport}
                    startDate={date}
                    onOpenMatch={
                      openMatch
                    }
                  />
                ) : (
                  <Scoreboard
                    loading={
                      state.loading
                    }

                    error={
                      state.error
                    }

                    games={
                      state.games
                    }

                    onRetry={
                      refreshScores
                    }

                    onOpenMatch={
                      openMatch
                    }

                    onHydrateGames={
                      hydrateVisibleScores
                    }

                    onShowUpcoming={() => changeScoresView('upcoming')}
                  />
                )}
              </div>
            </section>

            <section
              className="coming-grid shell"
              aria-label="VolleyWire features"
            >
              {FEATURE_CARDS.map(
                (
                  item,
                  index
                ) => (
                  <article
                    id={item.title
                      .toLowerCase()
                      .replace(
                        /\s+/g,
                        '-'
                      )}

                    key={
                      item.title
                    }
                  >
                    <span>
                      {String(
                        index + 1
                      ).padStart(
                        2,
                        '0'
                      )}
                    </span>

                    <h2>
                      {item.title}
                    </h2>

                    <p>
                      {
                        item.description
                      }
                    </p>

                    <small>
                      {item.status}
                    </small>
                  </article>
                )
              )}
            </section>
          </>
        )}
      </main>

      <footer className="site-footer">
        <div className="shell">
          <strong>
            VolleyWire
          </strong>

          <span>
            The Home of College
            Volleyball
          </span>

          <small>
            Independent college
            volleyball coverage.
          </small>
        </div>
      </footer>

      {activeView ===
        'scores' &&
      selectedGame ? (
        <MatchCenter
          key={
            selectedGame.gameId ||
            selectedGame.id ||
            `${selectedGame.away?.name}-${selectedGame.home?.name}`
          }

          game={
            selectedGame
          }

          onClose={
            closeMatch
          }

          onSnapshot={
            reconcileMatchCenterSnapshot
          }
        />
      ) : null}
    </div>
  );
}
